import type { EntitySelectorConfig, EntitySelectorEntityType, FormFieldDefinition, FormFieldOption } from '../../types/formBuilder';

const SYSTEM_GENERATED_FIELD_NAMES = new Set(['hospitalCode', 'hcpCode', 'patientCode']);

export type UploadedFileValue = {
  name: string;
  size: number;
  type: string;
  data: string | ArrayBuffer | null;
};

export type FieldValue = string | number | boolean | string[] | Record<string, unknown>[] | UploadedFileValue | null | undefined;

const SYSTEM_GENERATED_FIELD_TYPES = new Set(['AUTO_SEQUENCE', 'AUTO_GENERATED', 'LOOKUP_READ_ONLY', 'SYSTEM_FIELD', 'READ_ONLY']);
const ENTITY_PICKER_FIELD_TYPES = new Set(['ENTITY_SELECTOR', 'DEPENDENT_DROPDOWN', 'LOOKUP']);

/**
 * Some forms configure entity pickers (ENTITY_SELECTOR, DEPENDENT_DROPDOWN, or a non-readonly
 * LOOKUP) via `fieldMapping.componentType` (e.g. "Hospital Registration") instead of the
 * live-search `entitySelectorConfig`. Synthesize an equivalent config from that componentType so
 * the same entity-lookup search UI works either way.
 */
export function resolveEntitySelectorConfig(field: FormFieldDefinition): EntitySelectorConfig | undefined {
  if (field.entitySelectorConfig) return field.entitySelectorConfig;
  if (!ENTITY_PICKER_FIELD_TYPES.has(field.fieldType)) return undefined;

  const componentType = field.fieldMapping?.componentType?.toUpperCase() ?? '';
  let entityType: EntitySelectorEntityType | undefined;
  if (componentType.includes('HOSPITAL')) entityType = 'HOSPITAL';
  else if (componentType.includes('HCP') || componentType.includes('DOCTOR')) entityType = 'HCP';
  else if (componentType.includes('PATIENT')) entityType = 'PATIENT';
  else if (componentType.includes('CASE')) entityType = 'CASE';

  if (!entityType) return undefined;

  return {
    entityType,
    multiSelect: false,
    searchEnabled: true,
    lazyLoading: true,
    autoMappingEnabled: false,
    hierarchyEnforcement: false,
  };
}

/**
 * True when the form already carries its own Hospital picker. Uses the same detection the field
 * renderer uses (ENTITY_SELECTOR config or a LOOKUP/DEPENDENT_DROPDOWN mapped via componentType),
 * so the runtime never injects a second Hospital control next to a configured one.
 */
export function hasHospitalEntityPicker(fields: FormFieldDefinition[] | undefined): boolean {
  return (fields ?? []).some(
    (field) => resolveEntitySelectorConfig(field)?.entityType?.toUpperCase() === 'HOSPITAL'
      && !isSystemGeneratedField(field),
  );
}

/**
 * A form that carries both a Hospital picker and an HCP picker must offer only the HCPs of the
 * selected Hospital. Published forms configure the HCP picker without a `parentDependencyFieldId`
 * (e.g. a DEPENDENT_DROPDOWN mapped via `fieldMapping.componentType`), so the lookup was unfiltered.
 * Default that parent to the form's own Hospital picker; an explicit parent always wins.
 */
export function withDefaultEntityParents(fields: FormFieldDefinition[]): FormFieldDefinition[] {
  const hospitalField = fields.find(
    (field) => resolveEntitySelectorConfig(field)?.entityType?.toUpperCase() === 'HOSPITAL'
      && !isSystemGeneratedField(field),
  );
  if (!hospitalField) return fields;

  return fields.map((field) => {
    if (field === hospitalField || isSystemGeneratedField(field)) return field;
    const config = resolveEntitySelectorConfig(field);
    if (config?.entityType?.toUpperCase() !== 'HCP' || config.parentDependencyFieldId) return field;
    return {
      ...field,
      entitySelectorConfig: { ...config, parentDependencyFieldId: hospitalField.internalName },
    };
  });
}

export function isSystemGeneratedField(field: FormFieldDefinition | string): boolean {
  if (typeof field === 'string') {
    return SYSTEM_GENERATED_FIELD_NAMES.has(field);
  }
  if (SYSTEM_GENERATED_FIELD_TYPES.has(field.fieldType) || SYSTEM_GENERATED_FIELD_NAMES.has(field.internalName)) {
    return true;
  }
  // A LOOKUP field is a read-only derived display (e.g. "Hospital Name" shown once a Hospital is
  // picked elsewhere) only when marked read-only, or when it has no resolvable entity type to
  // search against. Otherwise it's the actual relationship picker and must stay editable.
  if (field.fieldType === 'LOOKUP') {
    if (field.properties?.readOnly === true || field.properties?.readonly === true) return true;
    return !resolveEntitySelectorConfig(field);
  }
  return false;
}

export function getSystemGeneratedFieldPlaceholder(field: FormFieldDefinition): string {
  if (!isSystemGeneratedField(field)) {
    return field.placeholder ?? '';
  }

  switch (field.internalName) {
    case 'hospitalCode':
      return 'Auto-generated when the hospital is created';
    case 'hcpCode':
      return 'Auto-generated when the HCP is created';
    case 'patientCode':
      return 'Auto-generated when the patient is created';
    default:
      return 'Auto-generated on save';
  }
}

function getActiveOptions(options?: FormFieldOption[]) {
  if (!options?.length) return [];
  return [...options]
    .filter((option) => option.enabled !== false)
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));
}

export function getFieldDefaultValue(field: FormFieldDefinition): FieldValue {
  const options = getActiveOptions(field.options);

  switch (field.fieldType) {
    case 'CHECKBOX':
      return field.defaultValue === 'true' || (field.defaultValue as unknown) === true;
    case 'TERMS_AND_CONDITIONS':
      return false;
    case 'PARAGRAPH':
    case 'FIELD_GROUP':
    case 'HEADER':
    case 'DISPLAY':
    case 'COMPUTED':
      return undefined; // no value — display-only or computed at runtime
    case 'AUTOPOPULATE':
      return ''; // value fetched dynamically at render time
    case 'RICHTEXT':
      return field.defaultValue ?? '';
    case 'IMAGE':
      return ''; // stores S3 file key
    case 'REPEATABLE_GROUP':
      return []; // dynamic array, starts empty
    case 'PASSWORD':
      return '';
    case 'MULTISELECT': {
      if (field.defaultValue) {
        try {
          return JSON.parse(field.defaultValue as string);
        } catch {
          return [field.defaultValue];
        }
      }

      const defaults = options.filter((option) => option.isDefault).map((option) => option.value);
      return defaults.length ? defaults : [];
    }
    case 'RADIO':
    case 'DROPDOWN': {
      if (field.defaultValue) return field.defaultValue;
      const defaultOption = options.find((option) => option.isDefault);
      return defaultOption?.value ?? '';
    }
    case 'DATE': {
      const autoRule = field.validationRules?.find((rule) => rule.type === 'AUTO_POPULATE');
      if (autoRule) return new Date().toISOString().split('T')[0];
      return field.defaultValue ?? '';
    }
    case 'NUMBER':
      return field.defaultValue !== undefined && field.defaultValue !== null
        ? Number(field.defaultValue)
        : '';
    default:
      return field.defaultValue ?? '';
  }
}

export function validateFieldValue(field: FormFieldDefinition, value: FieldValue): string | null {
  if (isSystemGeneratedField(field)) return null;
  if (!field.mandatory) return null;

  switch (field.fieldType) {
    case 'MULTISELECT':
      return !Array.isArray(value) || value.length === 0
        ? `${field.label} is required`
        : null;
    case 'CHECKBOX':
      return value !== true ? `${field.label} must be checked` : null;
    case 'TERMS_AND_CONDITIONS':
      return value !== true ? 'You must accept the terms and conditions' : null;
    case 'FILE':
      return !value ? `${field.label} is required` : null;
    case 'PARAGRAPH':
    case 'FIELD_GROUP':
      return null; // never validated
    case 'REPEATABLE_GROUP':
      return !Array.isArray(value) || value.length === 0
        ? `${field.label} requires at least one entry`
        : null;
    case 'PASSWORD': {
      if (value === undefined || value === null || value === '')
        return `${field.label} is required`;
      return null;
    }
    default:
      return value === undefined || value === null || value === ''
        ? `${field.label} is required`
        : null;
  }
}